import {SessionInstance} from '../instancesLogApi';
import UserData from '../../store/UserData';
import {MixpanelLogEvent} from '@amplify/athena-models';
import {v4} from 'uuid';

export default class LoggingApi {
    static getSessionId = async (): Promise<string> => {
        const response = await SessionInstance.post('');

        return response?.data?.session;
    };

    /**
     * A pre-consent funnel event.
     *
     * The payload is constructed from scratch rather than filtered out of the store: an
     * allowlist cannot leak a field somebody adds to UserData later, a denylist can. Nothing
     * here identifies the visitor — no name, email, phone, DOB, zip, income, answers,
     * qualification results, or the application id.
     */
    static logAnonymous = async (payload: {
        action: string;
        distinctId: string;
        entrySlide: string;
        experiment: Record<string, boolean | string>;
        utm: Record<string, string>;
        params: Record<string, string>;
        version: string;
    }): Promise<undefined> => {
        const response = await SessionInstance.post(`/${payload.action}/sqs`, {
            action: payload.action,
            distinct_id: payload.distinctId,
            $insert_id: v4(),
            time: Date.now(),
            sendingApp: 'WALKTHROUGH',
            // Marks the cohort so these are analysed separately rather than blended with
            // identified sessions, where they would double-count anyone who consents midway.
            // Read at the top level by athena, which withholds the IP when it sees it.
            consent_state: 'pending',
            // Nested under `data` with the same names the identified events use, because that is
            // where they land in Mixpanel: properties.data.experiment, .utm, .version. A funnel
            // that spans the consent boundary has to filter on one field, not two.
            // Sessions resume from where they left off, so a returning visitor enters the funnel
            // mid-way. Recording the entry point lets first-visit funnels be isolated. Top level,
            // where the identified events put their eventOrigin.
            entry_slide: payload.entrySlide,
            // Sanitised, so prefill values and click identifiers never travel. Top level, which
            // is where logAction puts params — it sets them on the wrapper, not on the store.
            params: payload.params,
            data: {
                // Which arm the visitor was served. Without it this cohort shows conversion but
                // cannot attribute it, so the experiments are unmeasurable for the majority who
                // never answer the banner. Assignment is random and says nothing about the person.
                //
                // The whole set travels rather than a curated subset: a flag pinned to 0 or 1 is
                // identical for everyone and so distinguishes nobody, and only the handful that
                // are genuinely split carry any information at all. If many run at once that
                // stops being true — see the note in LogStore.logAnonymousAction.
                experiment: payload.experiment,
                // Campaign fields only — source, medium, campaign, content, term, origin,
                // adgroup. Carried on the event rather than set on the profile because the
                // campaign a visitor arrived through belongs to the visit: the same person can
                // arrive twice from two sources, and a profile property would keep only the last.
                utm: payload.utm,
                // UserData.version, the same value and format the identified events carry
                // ("prod_0.0.18"). Taken from there rather than rebuilt from an env var, so the
                // two cohorts split by the same field. It describes the build, not the person.
                version: payload.version,
            },
        });

        return response.data;
    };

    /**
     * Asks the backend to join this visit's pre-consent session to the identified profile.
     *
     * Sent once, when analytics consent is granted. Whether it is acted on is the backend's
     * decision — the merge sits behind a secret there and is irreversible — so this is a
     * request rather than an instruction, and a failure is not worth interrupting the visit for.
     */
    static mergeIdentity = async (payload: {anonymousId: string; distinctId: string}): Promise<undefined> => {
        const response = await SessionInstance.post('/merge-identity', {
            anonymous_id: payload.anonymousId,
            distinct_id: payload.distinctId,
        });

        return response.data;
    };

    static log = async (
        userId: string,
        data: {action: string; data: UserData},
        {useQueue = true} = {},
    ): Promise<undefined> => {
        const mixpanelData: MixpanelLogEvent = {
            ...data,
            userId,
            distinct_id: userId,
            $insert_id: v4(),
            time: Date.now(),
            sendingApp: 'WALKTHROUGH',
        };

        const loggerUrl = useQueue ? `/${data.action}/sqs` : `/${data.action}`;

        const response = await SessionInstance.post(loggerUrl, mixpanelData);

        return response.data;
    };
}
